import { formatForContext } from "@/features/hoverIdle/resources/numberFormat";
import { NUMBER_LIMIT_BOUNDARY } from "@/features/hoverIdle/core/numberLimitBoundary";
import type { HugeNumber } from "@/shared/numbers";
import { compareHuge, huge, hugeZero } from "@/shared/numbers";
import type {
  NHovBranchId,
  NHovBranchRewardPreview,
  NHovSnapshot,
  NHovState,
} from "../types";
import {
  createDefaultNHovBranchExperienceByBranch,
  createDefaultNHovEffectBoundaryCaps,
  isNHovBranchAvailable,
  type LegacyNHovBranchEffectOverrides,
  NHOV_BRANCH_IDS,
  normalizeNHovEffectBoundaryCaps,
  normalizeNHovContinuousEffectProgressByBranch,
} from "./nHovBranches";
import {
  resolveNHovBranchEffects,
  type NHovBranchEffects,
  type NHovBranchGrowthExponentBonus,
} from "./nHovBranchEffects";
import {
  resolveNHovCandidateOffer,
  type NHovCandidateOffer,
} from "./nHovCandidateTheme";
import { resolveHgDiscountBoundaryReexecutionState } from "./hgDiscountBoundaryReexecution";
import {
  calculateNHovBranchLevel,
  calculateNHovBranchContinuousLevel,
  calculateNHovBranchLevelProgressRatio,
  calculateProspectiveNHovBranchLevel,
  resolveNHovBranchExperienceGrowth,
  resolveNHovBranchContinuousLevelsFromExperience,
  resolveNHovBranchLevelsFromExperience,
} from "./nHovGrowthLogic";
import { resolveNHovExperienceForEffect } from "./nHovContinuousProgress";
import { NHOV_BASE_HOV_THRESHOLD_VALUE } from "./nHovConstants";
import { resolveFreshnessWallNHovStalingExponent } from "../wall/freshnessWallLogic";
import type { NHovBalance } from "@/features/hoverIdle/balance/hovResetBalanceTypes";

// Compared against `runBaseHov`, never the historical max Hov reached during
// the NHov interval. Tune via `NHOV_BASE_HOV_THRESHOLD` in nHovConstants.
export const NHOV_UNLOCK_RUN_BASE_HOV = NHOV_BASE_HOV_THRESHOLD_VALUE;

// The first NHov is always hgAmplify; require it to land a visible branch
// level improvement rather than tuning a second fixed Hov amount.
export const NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL = 3;

export function createDefaultNHovState(): NHovState {
  return {
    count: 0,
    branchExperienceByBranch: createDefaultNHovBranchExperienceByBranch(),
    effectBoundaryCaps: createDefaultNHovEffectBoundaryCaps(),
  };
}

export function migrateLegacyNHovBranchEffectOverrides(options: {
  readonly branchExperienceByBranch: NHovState["branchExperienceByBranch"];
  readonly effectBoundaryCaps?: NHovState["effectBoundaryCaps"];
  readonly branchEffectOverrides: LegacyNHovBranchEffectOverrides;
  readonly hngRankScaleBaseBonus?: number;
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  readonly uncapped?: boolean;
  readonly balance?: NHovBalance;
}): NHovState["branchExperienceByBranch"] {
  let migrated = options.branchExperienceByBranch;
  const effectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
    options.effectBoundaryCaps,
  );
  for (const branchId of NHOV_BRANCH_IDS) {
    const targetEffect = options.branchEffectOverrides[branchId];
    if (targetEffect === undefined) continue;
    if (branchId === "hgDiscount" && effectBoundaryCaps.hgDiscountCostDivisor) {
      continue;
    }
    const upperLevel = calculateNHovBranchLevel({
      experience: migrated[branchId],
    });
    const neutralLevels = resolveNHovBranchLevelsFromExperience(migrated);
    const neutralContinuousLevels =
      resolveNHovBranchContinuousLevelsFromExperience(migrated);
    const resolveEffectAtLevel = (level: number): HugeNumber =>
      resolveNHovBranchEffectMetric(
        branchId,
        resolveNHovBranchEffects({
          branches: { ...neutralLevels, [branchId]: Math.floor(level) },
          balance: options.balance,
          numberLimitCandidateLevels: {
            ...neutralContinuousLevels,
            [branchId]: level,
          },
          effectLevels: {
            ...neutralContinuousLevels,
            [branchId]: level,
          },
          hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
          growthExponentBonus: options.growthExponentBonus,
          uncapped: options.uncapped,
          effectBoundaryCaps,
        }),
      );
    migrated = {
      ...migrated,
      [branchId]: resolveNHovExperienceForEffect({
        currentContinuousLevel: 0,
        freshNextContinuousLevel: upperLevel,
        targetEffect,
        additiveIdentityOffset: branchId === "hgNHovEffectsToHng" ? 1 : 0,
        resolveEffectAtLevel,
      }),
    };
  }
  return migrated;
}

export function resolveNHovEffectLevels(
  nHovState: NHovState,
  derivedBranchLevels: ReturnType<typeof resolveNHovBranchLevelsFromExperience>,
  continuousLevels: ReturnType<
    typeof resolveNHovBranchContinuousLevelsFromExperience
  >,
): ReturnType<typeof resolveNHovBranchContinuousLevelsFromExperience> {
  const continuousProgress = normalizeNHovContinuousEffectProgressByBranch(
    nHovState.continuousEffectProgressByBranch,
  );
  return {
    hgAmplify: continuousProgress.hgAmplify
      ? continuousLevels.hgAmplify
      : derivedBranchLevels.hgAmplify,
    hgDiscount: continuousProgress.hgDiscount
      ? continuousLevels.hgDiscount
      : derivedBranchLevels.hgDiscount,
    hngScale: continuousProgress.hngScale
      ? continuousLevels.hngScale
      : derivedBranchLevels.hngScale,
    hgNHovEffectsToHng: continuousProgress.hgNHovEffectsToHng
      ? continuousLevels.hgNHovEffectsToHng
      : derivedBranchLevels.hgNHovEffectsToHng,
  };
}

// `runBaseHov` must be the current Hov reset loop's earned Hov
// (`mHov.currentLoopHovEarned`), never the historical max for the interval.
export function isNHovUnlocked(
  runBaseHov: HugeNumber,
  balance?: NHovBalance,
): boolean {
  return (
    compareHuge(
      runBaseHov,
      balance?.baseHovThreshold ?? NHOV_UNLOCK_RUN_BASE_HOV,
    ) >= 0
  );
}

export function canExecuteNHov(options: {
  readonly runBaseHov: HugeNumber;
  readonly balance?: NHovBalance;
}): boolean {
  return isNHovUnlocked(options.runBaseHov, options.balance);
}

export interface CanExecuteNHovBranchByLevelOptions {
  readonly currentBranchLevel: number;
  readonly prospectiveBranchLevel: number;
}

// Pure branch-level lock: a branch is executable only when this run's branch
// experience gain raises that branch's derived level. `>` (not `>=`) so an
// NHov that does not advance the selected branch level stays locked.
export function canExecuteNHovBranchByLevel(
  options: CanExecuteNHovBranchByLevelOptions,
): boolean {
  if (
    !Number.isFinite(options.currentBranchLevel) ||
    options.currentBranchLevel < 0 ||
    !Number.isFinite(options.prospectiveBranchLevel)
  ) {
    return false;
  }
  return options.prospectiveBranchLevel > options.currentBranchLevel;
}

export interface CanExecuteNHovBranchOptions {
  readonly branchId: NHovBranchId;
  readonly nHovState: NHovState;
  // Hov earned in the current Hov reset loop (`mHov.currentLoopHovEarned`).
  readonly runBaseHov: HugeNumber;
  // True when an active Wall blocks this branch. Authoritative — callers must
  // honor this both in the snapshot's `canExecute` flag and in the model's
  // execution guard so UI and execution cannot drift.
  readonly isBlockedByActiveWall?: boolean;
  readonly firstExecutionBranchSelectionUnlocked?: boolean;
  readonly hngRankScaleBaseBonus?: number;
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  readonly uncapped?: boolean;
  readonly freshnessExponent?: number;
  readonly resolvedProspectiveEffects?: ProspectiveNHovBranchEffects;
  readonly balance?: NHovBalance;
}

// Used by both the snapshot and `executeNHov` so UI canExecute and model
// execution outcome cannot drift.
export function canExecuteNHovBranch(
  options: CanExecuteNHovBranchOptions,
): boolean {
  const {
    branchId,
    nHovState,
    runBaseHov,
    isBlockedByActiveWall,
    firstExecutionBranchSelectionUnlocked,
  } = options;
  if (isBlockedByActiveWall) {
    return false;
  }
  if (!canExecuteNHov({ runBaseHov, balance: options.balance })) {
    return false;
  }
  if (
    !isNHovBranchAvailable({
      branchId,
      nHovCount: nHovState.count,
      firstExecutionBranchSelectionUnlocked,
    })
  ) {
    return false;
  }
  const currentExperience = nHovState.branchExperienceByBranch[branchId];
  const boundaryCappedHgDiscount =
    branchId === "hgDiscount" &&
    normalizeNHovEffectBoundaryCaps(nHovState.effectBoundaryCaps)
      .hgDiscountCostDivisor;
  if (
    boundaryCappedHgDiscount &&
    !canExecuteBoundaryCappedNHovHgDiscount({
      runBaseHov,
      balance: options.balance,
    })
  ) {
    return false;
  }
  if (boundaryCappedHgDiscount && options.freshnessExponent === undefined) {
    return true;
  }
  if (!boundaryCappedHgDiscount) {
    const currentBranchLevel = calculateNHovBranchLevel({
      experience: currentExperience,
    });
    const prospectiveBranchLevel = calculateProspectiveNHovBranchLevel({
      currentExperience,
      runBaseHov,
      balance: options.balance,
    });
    if (
      !canExecuteNHovBranchByLevel({
        currentBranchLevel,
        prospectiveBranchLevel,
      })
    ) {
      return false;
    }
    if (
      nHovState.count === 0 &&
      branchId === "hgAmplify" &&
      prospectiveBranchLevel <
        (options.balance?.firstHgAmplifyMinBranchLevel ??
          NHOV_FIRST_HG_AMPLIFY_MIN_BRANCH_LEVEL)
    ) {
      return false;
    }
  }
  if (options.freshnessExponent !== undefined) {
    const prospective =
      options.resolvedProspectiveEffects ??
      resolveProspectiveNHovBranchEffects({
        branchId,
        nHovState,
        runBaseHov,
        hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
        growthExponentBonus: options.growthExponentBonus,
        uncapped: options.uncapped,
        freshnessExponent: options.freshnessExponent,
        balance: options.balance,
      });
    return (
      compareHuge(
        resolveNHovBranchEffectMetric(branchId, prospective.nextEffects),
        resolveNHovBranchEffectMetric(branchId, prospective.currentEffects),
      ) > 0
    );
  }
  return true;
}

function canExecuteBoundaryCappedNHovHgDiscount(options: {
  readonly runBaseHov: HugeNumber;
  readonly balance?: NHovBalance;
}): boolean {
  return resolveHgDiscountBoundaryReexecutionState({
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  }).canExecute;
}

const NHOV_UNLOCK_LOG10 = Math.log10(NHOV_UNLOCK_RUN_BASE_HOV);

// log10 scaling keeps the progress bar readable when Hov is many orders of
// magnitude away from the unlock threshold.
export function calculateNHovProgressRatio(
  runBaseHov: HugeNumber,
  balance?: NHovBalance,
): number {
  const unlockRunBaseHov =
    balance?.baseHovThreshold ?? NHOV_UNLOCK_RUN_BASE_HOV;
  if (!runBaseHov.isFinite()) return 1;
  if (compareHuge(runBaseHov, 0) <= 0) return 0;
  if (compareHuge(runBaseHov, unlockRunBaseHov) >= 0) {
    return 1;
  }
  const targetLog =
    balance === undefined ? NHOV_UNLOCK_LOG10 : Math.log10(unlockRunBaseHov);
  if (!Number.isFinite(targetLog) || targetLog <= 0) return 0;
  const valueLog = runBaseHov.log10().toNumber();
  if (!Number.isFinite(valueLog) || valueLog <= 0) return 0;
  return clamp01(valueLog / targetLog);
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  if (value <= 0) return 0;
  if (value >= 1) return 1;
  return value;
}

// Branch preview formatters delegate the numeric part to `formatForContext` so
// the active `settings.notation` (standard / scientific / engineering) drives
// the digit shape, while branch-specific prefixes (`x`, `/`) and sign markers
// stay constant. Keeping the prefix outside the formatter preserves the
// "preview is a multiplier/divisor" meaning without depending on notation.
function formatPreviewNumber(value: HugeNumber): string {
  return formatForContext(value, "compact");
}

function formatMultiplier(value: HugeNumber): string {
  return `x${formatPreviewNumber(value)}`;
}

function formatHgCostDivisor(value: HugeNumber): string {
  return `/${formatPreviewNumber(value)}`;
}

function formatHgCostDivisorGain(
  current: HugeNumber,
  next: HugeNumber,
): string {
  if (compareHuge(current, 0) <= 0) return formatHgCostDivisor(next);
  return formatMultiplier(next.div(current));
}

function formatRankScaleBase(value: HugeNumber): string {
  return `x${formatPreviewNumber(value)}`;
}

function formatSignedNumber(value: HugeNumber): string {
  const sign = compareHuge(value, 0) >= 0 ? "+" : "-";
  return `${sign}${formatPreviewNumber(value.abs())}`;
}

function formatTransferMultiplier(value: HugeNumber): string {
  if (compareHuge(value, 0) === 0) return "x0";
  return formatMultiplier(value);
}

interface BuildPreviewContext {
  readonly nHovState: NHovState;
  readonly runBaseHov: HugeNumber;
  readonly offer: NHovCandidateOffer;
  // Forwarded to resolveNHovBranchEffects so the hngScale branch preview
  // reflects acquired Hov skill bonuses, matching the model's real HnG gain.
  readonly hngRankScaleBaseBonus: number;
  // Forwarded to resolveNHovBranchEffects so the preview growth matches the
  // model's real branch effects under acquired Hov skills.
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  // Branch-id resolver for active-Wall execution blocks. The snapshot side
  // routes the model's authoritative wall state through this so locked /
  // canExecute flags stay aligned with the model's execution guard.
  readonly isBranchBlockedByActiveWall: (branchId: NHovBranchId) => boolean;
  readonly firstExecutionBranchSelectionUnlocked: boolean;
  // Lifts the NUMBER_LIMIT_BOUNDARY clamp on branch effects (granted by
  // `outside-nhov`) so the preview grows past the JS number limit.
  readonly uncapped: boolean;
  readonly freshnessWallActive: boolean;
  readonly freshnessExponent: number;
  readonly balance?: NHovBalance;
}

export interface ResolveProspectiveNHovBranchEffectsOptions {
  readonly branchId: NHovBranchId;
  readonly nHovState: NHovState;
  // Current Hov reset loop's earned Hov (`mHov.currentLoopHovEarned`); drives
  // the prospective branch-experience gain used by the UI preview's "next"
  // column and the NHov MP execution improvement-ratio gate.
  readonly runBaseHov: HugeNumber;
  // Forwarded to `resolveNHovBranchEffects` so the hngScale branch effect
  // reflects acquired Hov skill bonuses. Defaults to no bonus for callers
  // without skill context.
  readonly hngRankScaleBaseBonus?: number;
  // Forwarded to `resolveNHovBranchEffects` so prospective branch growth
  // reflects acquired Hov skill bonuses. Defaults to neutral.
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  // When true, the prospective level collapses to the current level so the
  // "next" effect equals the current one. The UI uses this to keep locked
  // cells from suggesting progress the lock would prevent. Auto-execute
  // gates skip locked branches via `canExecuteNHovReset` earlier, so model
  // callers can leave this unset.
  readonly suppressProspectiveAdvance?: boolean;
  // When true (granted by `outside-nhov`), branch effects are no longer clamped
  // at NUMBER_LIMIT_BOUNDARY, so the preview / gate grow past the JS limit.
  readonly uncapped?: boolean;
  readonly freshnessExponent?: number;
  readonly balance?: NHovBalance;
}

export interface ProspectiveNHovBranchEffects {
  readonly currentEffects: NHovBranchEffects;
  readonly freshNextEffects: NHovBranchEffects;
  readonly nextEffects: NHovBranchEffects;
  readonly currentLevel: number;
  readonly freshNextLevel: number;
  readonly nextLevel: number;
  readonly experienceGain: HugeNumber;
  readonly experienceAfter: HugeNumber;
}

export function resolveNHovBranchEffectMetric(
  branchId: NHovBranchId,
  effects: NHovBranchEffects,
): HugeNumber {
  switch (branchId) {
    case "hgAmplify":
      return effects.hgAmplifyMultiplier;
    case "hgDiscount":
      return effects.hgDiscountCostDivisor;
    case "hngScale":
      return effects.hngRankScaleBase;
    case "hgNHovEffectsToHng":
      return effects.hgNHovEffectsToHngTransferMultiplier;
  }
}

export function applyNHovFreshnessStalingToResolvedEffect(options: {
  readonly branchId: NHovBranchId;
  readonly currentEffect: HugeNumber;
  readonly freshNextEffect: HugeNumber;
  readonly freshnessExponent: number;
}): HugeNumber {
  const exponent =
    Number.isFinite(options.freshnessExponent) && options.freshnessExponent > 0
      ? Math.min(1, options.freshnessExponent)
      : 1;
  if (
    exponent >= 1 ||
    compareHuge(options.freshNextEffect, options.currentEffect) <= 0
  ) {
    return options.freshNextEffect;
  }

  const current =
    options.branchId === "hgNHovEffectsToHng"
      ? options.currentEffect.add(1)
      : options.currentEffect;
  const fresh =
    options.branchId === "hgNHovEffectsToHng"
      ? options.freshNextEffect.add(1)
      : options.freshNextEffect;
  if (compareHuge(current, 0) <= 0 || compareHuge(fresh, 0) <= 0) {
    return options.freshNextEffect;
  }
  const effective = current.mul(fresh.div(current).pow(exponent));
  if (!effective.isFinite()) return options.freshNextEffect;
  return options.branchId === "hgNHovEffectsToHng"
    ? effective.sub(1)
    : effective;
}

// Single source of truth for `current -> next` NHov branch effects. Both the
// UI reward preview and the NHov MP execution improvement-ratio gate go
// through this helper so they can never drift on what "next" means.
export function resolveProspectiveNHovBranchEffects(
  options: ResolveProspectiveNHovBranchEffectsOptions,
): ProspectiveNHovBranchEffects {
  const currentEffectBoundaryCaps = normalizeNHovEffectBoundaryCaps(
    options.nHovState.effectBoundaryCaps,
  );
  const derivedBranchLevels = resolveNHovBranchLevelsFromExperience(
    options.nHovState.branchExperienceByBranch,
  );
  const currentContinuousLevels =
    resolveNHovBranchContinuousLevelsFromExperience(
      options.nHovState.branchExperienceByBranch,
    );
  const currentEffectLevels = resolveNHovEffectLevels(
    options.nHovState,
    derivedBranchLevels,
    currentContinuousLevels,
  );
  if (
    options.branchId === "hgDiscount" &&
    currentEffectBoundaryCaps.hgDiscountCostDivisor
  ) {
    return resolveBoundaryCappedHgDiscountProspectiveEffects({
      options,
      currentEffectBoundaryCaps,
      derivedBranchLevels,
      currentContinuousLevels,
      currentEffectLevels,
    });
  }
  const currentLevel = derivedBranchLevels[options.branchId];
  const currentExperience =
    options.nHovState.branchExperienceByBranch[options.branchId];
  const experienceGrowth = resolveNHovBranchExperienceGrowth({
    currentExperience,
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  });
  const freshProspectiveLevel = calculateNHovBranchLevel({
    experience: experienceGrowth.experienceAfter,
  });
  const freshNextLevel = options.suppressProspectiveAdvance
    ? currentLevel
    : freshProspectiveLevel;
  const freshnessAdjusted = options.freshnessExponent !== undefined;
  const freshNextBranchExperienceByBranch = options.suppressProspectiveAdvance
    ? options.nHovState.branchExperienceByBranch
    : {
        ...options.nHovState.branchExperienceByBranch,
        [options.branchId]: experienceGrowth.experienceAfter,
      };
  const freshNextContinuousLevels = options.suppressProspectiveAdvance
    ? currentContinuousLevels
    : resolveNHovBranchContinuousLevelsFromExperience(
        freshNextBranchExperienceByBranch,
      );
  const nextEffectBoundaryCaps =
    options.suppressProspectiveAdvance || options.branchId !== "hgDiscount"
      ? currentEffectBoundaryCaps
      : createDefaultNHovEffectBoundaryCaps();
  const currentEffects = resolveNHovBranchEffects({
    branches: derivedBranchLevels,
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: options.uncapped,
    numberLimitCandidateLevels: currentContinuousLevels,
    effectLevels: currentEffectLevels,
    effectBoundaryCaps: currentEffectBoundaryCaps,
  });
  if (options.suppressProspectiveAdvance) {
    return {
      currentEffects,
      freshNextEffects: currentEffects,
      nextEffects: currentEffects,
      currentLevel,
      freshNextLevel: currentLevel,
      nextLevel: freshnessAdjusted
        ? currentEffectLevels[options.branchId]
        : currentLevel,
      experienceGain: experienceGrowth.experienceGain,
      experienceAfter: experienceGrowth.experienceAfter,
    };
  }
  const freshEffectLevels = {
    ...currentEffectLevels,
    [options.branchId]: freshNextLevel,
  };
  const freshNextEffects = resolveNHovBranchEffects({
    branches: {
      ...derivedBranchLevels,
      [options.branchId]: freshNextLevel,
    },
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: options.uncapped,
    numberLimitCandidateLevels: freshNextContinuousLevels,
    effectLevels: freshEffectLevels,
    effectBoundaryCaps: nextEffectBoundaryCaps,
  });
  if (!freshnessAdjusted) {
    return {
      currentEffects,
      freshNextEffects,
      nextEffects: freshNextEffects,
      currentLevel,
      freshNextLevel,
      nextLevel: freshNextLevel,
      experienceGain: experienceGrowth.experienceGain,
      experienceAfter: experienceGrowth.experienceAfter,
    };
  }

  const currentEffect = resolveNHovBranchEffectMetric(
    options.branchId,
    currentEffects,
  );
  const freshNextEffect = resolveNHovBranchEffectMetric(
    options.branchId,
    freshNextEffects,
  );
  const effectiveNextEffect = applyNHovFreshnessStalingToResolvedEffect({
    branchId: options.branchId,
    currentEffect,
    freshNextEffect,
    freshnessExponent: options.freshnessExponent,
  });
  if (compareHuge(effectiveNextEffect, currentEffect) <= 0) {
    return {
      currentEffects,
      freshNextEffects,
      nextEffects: currentEffects,
      currentLevel,
      freshNextLevel,
      nextLevel: currentContinuousLevels[options.branchId],
      experienceGain: hugeZero(),
      experienceAfter: currentExperience,
    };
  }

  const resolveEffectAtLevel = (level: number): HugeNumber =>
    resolveNHovBranchEffectMetric(
      options.branchId,
      resolveNHovBranchEffects({
        branches: {
          ...derivedBranchLevels,
          [options.branchId]: Math.floor(level),
        },
        balance: options.balance,
        hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
        growthExponentBonus: options.growthExponentBonus,
        uncapped: options.uncapped,
        numberLimitCandidateLevels: {
          ...currentEffectLevels,
          [options.branchId]: level,
        },
        effectLevels: {
          ...currentEffectLevels,
          [options.branchId]: level,
        },
        effectBoundaryCaps: nextEffectBoundaryCaps,
      }),
    );
  const experienceAfter = resolveNHovExperienceForEffect({
    currentContinuousLevel: currentEffectLevels[options.branchId],
    freshNextContinuousLevel: freshNextContinuousLevels[options.branchId],
    targetEffect: effectiveNextEffect,
    additiveIdentityOffset: options.branchId === "hgNHovEffectsToHng" ? 1 : 0,
    resolveEffectAtLevel,
  });
  const nextBranchExperienceByBranch = {
    ...options.nHovState.branchExperienceByBranch,
    [options.branchId]: experienceAfter,
  };
  const nextContinuousLevels = resolveNHovBranchContinuousLevelsFromExperience(
    nextBranchExperienceByBranch,
  );
  const nextEffectLevels = {
    ...currentEffectLevels,
    [options.branchId]: nextContinuousLevels[options.branchId],
  };
  const nextEffects = resolveNHovBranchEffects({
    branches: {
      ...derivedBranchLevels,
      [options.branchId]: Math.floor(nextContinuousLevels[options.branchId]),
    },
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: options.uncapped,
    numberLimitCandidateLevels: nextContinuousLevels,
    effectLevels: nextEffectLevels,
    effectBoundaryCaps: nextEffectBoundaryCaps,
  });
  return {
    currentEffects,
    freshNextEffects,
    nextEffects,
    currentLevel,
    freshNextLevel,
    nextLevel: nextContinuousLevels[options.branchId],
    experienceGain: experienceAfter.sub(currentExperience),
    experienceAfter,
  };
}

function resolveBoundaryCappedHgDiscountProspectiveEffects(context: {
  readonly options: ResolveProspectiveNHovBranchEffectsOptions;
  readonly currentEffectBoundaryCaps: ReturnType<
    typeof normalizeNHovEffectBoundaryCaps
  >;
  readonly derivedBranchLevels: ReturnType<
    typeof resolveNHovBranchLevelsFromExperience
  >;
  readonly currentContinuousLevels: ReturnType<
    typeof resolveNHovBranchContinuousLevelsFromExperience
  >;
  readonly currentEffectLevels: ReturnType<typeof resolveNHovEffectLevels>;
}): ProspectiveNHovBranchEffects {
  const {
    options,
    currentEffectBoundaryCaps,
    derivedBranchLevels,
    currentContinuousLevels,
    currentEffectLevels,
  } = context;
  const freshnessAdjusted = options.freshnessExponent !== undefined;
  const currentEffects = resolveNHovBranchEffects({
    branches: derivedBranchLevels,
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: options.uncapped,
    numberLimitCandidateLevels: currentContinuousLevels,
    effectLevels: currentEffectLevels,
    effectBoundaryCaps: currentEffectBoundaryCaps,
  });
  const experienceGrowth = resolveNHovBranchExperienceGrowth({
    currentExperience: huge(0),
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  });
  if (options.suppressProspectiveAdvance) {
    return {
      currentEffects,
      freshNextEffects: currentEffects,
      nextEffects: currentEffects,
      currentLevel: derivedBranchLevels.hgDiscount,
      freshNextLevel: derivedBranchLevels.hgDiscount,
      nextLevel: freshnessAdjusted
        ? currentEffectLevels.hgDiscount
        : derivedBranchLevels.hgDiscount,
      experienceGain: hugeZero(),
      experienceAfter: options.nHovState.branchExperienceByBranch.hgDiscount,
    };
  }

  const boundaryState = resolveHgDiscountBoundaryReexecutionState({
    runBaseHov: options.runBaseHov,
    balance: options.balance,
  });
  const freshNextLevel = boundaryState.canExecute
    ? boundaryState.freshIntegerLevel
    : derivedBranchLevels.hgDiscount;
  const freshNextEffects = resolveNHovBranchEffects({
    branches: {
      ...derivedBranchLevels,
      hgDiscount: freshNextLevel,
    },
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: options.uncapped,
    numberLimitCandidateLevels: {
      ...currentContinuousLevels,
      hgDiscount: boundaryState.freshIntegerLevel,
    },
    effectLevels: { ...currentEffectLevels, hgDiscount: freshNextLevel },
    hgDiscountCostDivisorOverride: boundaryState.nextPreviewDivisor,
    effectBoundaryCaps: createDefaultNHovEffectBoundaryCaps(),
  });
  if (!freshnessAdjusted) {
    return {
      currentEffects,
      freshNextEffects,
      nextEffects: freshNextEffects,
      currentLevel: derivedBranchLevels.hgDiscount,
      freshNextLevel,
      nextLevel: freshNextLevel,
      experienceGain: experienceGrowth.experienceGain,
      experienceAfter: experienceGrowth.experienceGain,
    };
  }
  const currentEffect = currentEffects.hgDiscountCostDivisor;
  const effectiveNextEffect = applyNHovFreshnessStalingToResolvedEffect({
    branchId: "hgDiscount",
    currentEffect,
    freshNextEffect: freshNextEffects.hgDiscountCostDivisor,
    freshnessExponent: options.freshnessExponent,
  });
  if (compareHuge(effectiveNextEffect, currentEffect) <= 0) {
    return {
      currentEffects,
      freshNextEffects,
      nextEffects: currentEffects,
      currentLevel: derivedBranchLevels.hgDiscount,
      freshNextLevel,
      nextLevel: currentEffectLevels.hgDiscount,
      experienceGain: hugeZero(),
      experienceAfter: options.nHovState.branchExperienceByBranch.hgDiscount,
    };
  }
  const resolveEffectAtLevel = (level: number): HugeNumber =>
    resolveNHovBranchEffects({
      branches: { ...derivedBranchLevels, hgDiscount: Math.floor(level) },
      balance: options.balance,
      hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
      growthExponentBonus: options.growthExponentBonus,
      uncapped: true,
      numberLimitCandidateLevels: {
        ...currentEffectLevels,
        hgDiscount: level,
      },
      effectLevels: { ...currentEffectLevels, hgDiscount: level },
      effectBoundaryCaps: createDefaultNHovEffectBoundaryCaps(),
    }).hgDiscountCostDivisor;
  const currentEffectiveExperience = resolveNHovExperienceForEffect({
    currentContinuousLevel: 0,
    freshNextContinuousLevel: currentContinuousLevels.hgDiscount,
    targetEffect: currentEffect,
    resolveEffectAtLevel,
  });
  const currentEffectiveLevel = calculateNHovBranchContinuousLevel({
    experience: currentEffectiveExperience,
  });
  const experienceAfter = resolveNHovExperienceForEffect({
    currentContinuousLevel: currentEffectiveLevel,
    freshNextContinuousLevel: freshNextLevel,
    targetEffect: effectiveNextEffect,
    resolveEffectAtLevel,
  });
  const nextContinuousLevels = resolveNHovBranchContinuousLevelsFromExperience({
    ...options.nHovState.branchExperienceByBranch,
    hgDiscount: experienceAfter,
  });
  const nextEffectLevels = {
    ...currentEffectLevels,
    hgDiscount: nextContinuousLevels.hgDiscount,
  };
  const nextEffects = resolveNHovBranchEffects({
    branches: {
      ...derivedBranchLevels,
      hgDiscount: Math.floor(nextContinuousLevels.hgDiscount),
    },
    balance: options.balance,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus,
    growthExponentBonus: options.growthExponentBonus,
    uncapped: true,
    numberLimitCandidateLevels: nextContinuousLevels,
    effectLevels: nextEffectLevels,
    effectBoundaryCaps: createDefaultNHovEffectBoundaryCaps(),
  });

  return {
    currentEffects,
    freshNextEffects,
    nextEffects,
    currentLevel: derivedBranchLevels.hgDiscount,
    freshNextLevel,
    nextLevel: nextContinuousLevels.hgDiscount,
    experienceGain: experienceAfter.sub(
      options.nHovState.branchExperienceByBranch.hgDiscount,
    ),
    experienceAfter,
  };
}

// Locked branches collapse `next` to `current` so the UI never suggests
// progress the lock would prevent.
function buildBranchPreview(
  branchId: NHovBranchId,
  context: BuildPreviewContext,
): NHovBranchRewardPreview {
  const isAvailable = isNHovBranchAvailable({
    branchId,
    nHovCount: context.nHovState.count,
    firstExecutionBranchSelectionUnlocked:
      context.firstExecutionBranchSelectionUnlocked,
  });
  const isBlockedByActiveWall = context.isBranchBlockedByActiveWall(branchId);
  const unresolvedProspective = resolveProspectiveNHovBranchEffects({
    branchId,
    nHovState: context.nHovState,
    runBaseHov: context.runBaseHov,
    hngRankScaleBaseBonus: context.hngRankScaleBaseBonus,
    growthExponentBonus: context.growthExponentBonus,
    uncapped: context.uncapped,
    freshnessExponent: context.freshnessWallActive
      ? context.freshnessExponent
      : undefined,
    balance: context.balance,
  });
  const canExecute = canExecuteNHovBranch({
    branchId,
    nHovState: context.nHovState,
    runBaseHov: context.runBaseHov,
    isBlockedByActiveWall,
    firstExecutionBranchSelectionUnlocked:
      context.firstExecutionBranchSelectionUnlocked,
    hngRankScaleBaseBonus: context.hngRankScaleBaseBonus,
    growthExponentBonus: context.growthExponentBonus,
    uncapped: context.uncapped,
    freshnessExponent: context.freshnessWallActive
      ? context.freshnessExponent
      : undefined,
    resolvedProspectiveEffects: unresolvedProspective,
    balance: context.balance,
  });
  // Unavailable cells are not rendered, so "locked" only applies when the cell
  // is visible but currently un-executable.
  const isLocked = isAvailable && !canExecute;
  const currentExperience =
    context.nHovState.branchExperienceByBranch[branchId];
  // Active-Wall blocks stop branch experience accumulation in the preview so
  // the XP gauge cannot drift past the same `canExecuteNHovBranch` gate that
  // blocks `executeNHov`. Non-wall lock cases keep their pre-existing
  // "gauge advances toward the next executable state" behavior, so the XP
  // gauge preview experience is gated only by the wall block.
  // Locked cells (wall-blocked or otherwise) collapse `next` effects to
  // `current` so the UI never suggests progress the lock would prevent.
  const prospective = isLocked
    ? resolveProspectiveNHovBranchEffects({
        branchId,
        nHovState: context.nHovState,
        runBaseHov: context.runBaseHov,
        hngRankScaleBaseBonus: context.hngRankScaleBaseBonus,
        growthExponentBonus: context.growthExponentBonus,
        suppressProspectiveAdvance: true,
        uncapped: context.uncapped,
        freshnessExponent: context.freshnessWallActive
          ? context.freshnessExponent
          : undefined,
        balance: context.balance,
      })
    : unresolvedProspective;
  const {
    currentEffects: effects,
    freshNextEffects,
    nextEffects,
    experienceGain,
    experienceAfter,
  } = prospective;
  const xpGaugeExperience = isBlockedByActiveWall
    ? currentExperience
    : experienceAfter;
  const branchLevelProgressRatio = calculateNHovBranchLevelProgressRatio({
    experience: xpGaugeExperience,
  });
  const theme = context.offer.themesByBranchId[branchId];
  const growthPreview = {
    freshnessWallActive: context.freshnessWallActive,
    experienceGain,
    experienceAfter,
  };

  switch (branchId) {
    case "hgAmplify": {
      const currentValue = effects.hgAmplifyMultiplier;
      const freshNextValue = freshNextEffects.hgAmplifyMultiplier;
      const nextValue = nextEffects.hgAmplifyMultiplier;
      const gain =
        compareHuge(currentValue, 0) > 0
          ? nextValue.div(currentValue)
          : huge(1);
      return {
        branchId,
        theme,
        currentValueLabel: formatMultiplier(currentValue),
        gainValueLabel: formatMultiplier(gain),
        freshNextValueLabel: formatMultiplier(freshNextValue),
        nextValueLabel: formatMultiplier(nextValue),
        currentEffect: currentValue,
        freshNextEffect: freshNextValue,
        effectiveNextEffect: nextValue,
        ...growthPreview,
        isAvailable,
        canExecute,
        isLocked,
        branchLevelProgressRatio,
      };
    }
    case "hgDiscount": {
      const currentValue = effects.hgDiscountCostDivisor;
      const freshNextValue = freshNextEffects.hgDiscountCostDivisor;
      const nextValue = nextEffects.hgDiscountCostDivisor;
      // Pre-execution preview only: once the resolved `next` divisor is
      // cap-aware clamped to NUMBER_LIMIT_BOUNDARY, the next execution is
      // guaranteed to reach the cap, so the XP gauge reads full instead of the
      // partial saved+gain experience ratio. The post-execution boundary
      // re-execution region (cap flag already set) keeps its own gauge
      // behavior; an `uncapped` preview grows past the boundary and is excluded
      // by the exact-equality check.
      const isPreExecutionNextDivisorCapped =
        !normalizeNHovEffectBoundaryCaps(context.nHovState.effectBoundaryCaps)
          .hgDiscountCostDivisor &&
        compareHuge(nextValue, NUMBER_LIMIT_BOUNDARY) === 0;
      return {
        branchId,
        theme,
        currentValueLabel: formatHgCostDivisor(currentValue),
        gainValueLabel: formatHgCostDivisorGain(currentValue, nextValue),
        freshNextValueLabel: formatHgCostDivisor(freshNextValue),
        nextValueLabel: formatHgCostDivisor(nextValue),
        currentEffect: currentValue,
        freshNextEffect: freshNextValue,
        effectiveNextEffect: nextValue,
        ...growthPreview,
        isAvailable,
        canExecute,
        isLocked,
        branchLevelProgressRatio: isPreExecutionNextDivisorCapped
          ? 1
          : branchLevelProgressRatio,
      };
    }
    case "hngScale": {
      const currentBase = effects.hngRankScaleBase;
      const freshNextBase = freshNextEffects.hngRankScaleBase;
      const nextBase = nextEffects.hngRankScaleBase;
      const gainBase = nextBase.sub(currentBase);
      return {
        branchId,
        theme,
        currentValueLabel: formatRankScaleBase(currentBase),
        gainValueLabel: formatSignedNumber(gainBase),
        freshNextValueLabel: formatRankScaleBase(freshNextBase),
        nextValueLabel: formatRankScaleBase(nextBase),
        currentEffect: currentBase,
        freshNextEffect: freshNextBase,
        effectiveNextEffect: nextBase,
        ...growthPreview,
        isAvailable,
        canExecute,
        isLocked,
        branchLevelProgressRatio,
      };
    }
    case "hgNHovEffectsToHng": {
      const currentValue = effects.hgNHovEffectsToHngTransferMultiplier;
      const freshNextValue =
        freshNextEffects.hgNHovEffectsToHngTransferMultiplier;
      const nextValue = nextEffects.hgNHovEffectsToHngTransferMultiplier;
      const gain = nextValue.sub(currentValue);
      return {
        branchId,
        theme,
        currentValueLabel: formatTransferMultiplier(currentValue),
        gainValueLabel: formatSignedNumber(gain),
        freshNextValueLabel: formatTransferMultiplier(freshNextValue),
        nextValueLabel: formatTransferMultiplier(nextValue),
        currentEffect: currentValue,
        freshNextEffect: freshNextValue,
        effectiveNextEffect: nextValue,
        ...growthPreview,
        isAvailable,
        canExecute,
        isLocked,
        branchLevelProgressRatio,
      };
    }
  }
}

export interface CreateNHovBranchRewardPreviewsOptions {
  readonly nHovState: NHovState;
  readonly balance?: NHovBalance;
  // Current Hov reset loop's earned Hov (`mHov.currentLoopHovEarned`).
  readonly runBaseHov: HugeNumber;
  // Callers may pass a pre-resolved offer to keep candidate themes stable;
  // omitting it falls back to the deterministic offer for the current count.
  readonly offer?: NHovCandidateOffer;
  // Optional skill-derived bonus for the HnG rank scale base. Omit when no
  // skill context is available; the preview falls back to the pre-skill base.
  readonly hngRankScaleBaseBonus?: number;
  // Optional skill-derived growth exponent bonus per affected branch. Omit
  // when no skill context is available; the preview stays neutral.
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  // Optional active-Wall branch block resolver. When omitted, no branch is
  // treated as wall-blocked (callers without wall context need not opt in).
  readonly isBranchBlockedByActiveWall?: (branchId: NHovBranchId) => boolean;
  readonly firstExecutionBranchSelectionUnlocked?: boolean;
  // Lifts the NUMBER_LIMIT_BOUNDARY clamp on branch effects (granted by
  // `outside-nhov`). Defaults to clamped for callers without achievement context.
  readonly uncapped?: boolean;
  readonly freshnessStaling?: {
    readonly elapsedSinceHovRunMs: number;
  };
}

export function createNHovBranchRewardPreviews(
  options: CreateNHovBranchRewardPreviewsOptions,
): readonly NHovBranchRewardPreview[] {
  const offer =
    options.offer ?? resolveNHovCandidateOffer(options.nHovState.count);
  const isBranchBlockedByActiveWall =
    options.isBranchBlockedByActiveWall ?? (() => false);
  const context: BuildPreviewContext = {
    nHovState: options.nHovState,
    balance: options.balance,
    runBaseHov: options.runBaseHov,
    offer,
    hngRankScaleBaseBonus: options.hngRankScaleBaseBonus ?? 0,
    growthExponentBonus: options.growthExponentBonus,
    isBranchBlockedByActiveWall,
    firstExecutionBranchSelectionUnlocked:
      options.firstExecutionBranchSelectionUnlocked === true,
    uncapped: options.uncapped === true,
    freshnessWallActive: options.freshnessStaling !== undefined,
    freshnessExponent:
      options.freshnessStaling === undefined
        ? 1
        : resolveFreshnessWallNHovStalingExponent(
            options.freshnessStaling.elapsedSinceHovRunMs,
          ),
  };
  return NHOV_BRANCH_IDS.map((branchId) =>
    buildBranchPreview(branchId, context),
  );
}

export interface CreateNHovSnapshotOptions {
  readonly nHovState: NHovState;
  readonly balance?: NHovBalance;
  // Current Hov reset loop's earned Hov (`mHov.currentLoopHovEarned`), never
  // the historical max for the interval.
  readonly runBaseHov: HugeNumber;
  // Optional skill-derived bonus for the HnG rank scale base, threaded
  // through so the hngScale branch preview matches the real HnG gain.
  readonly hngRankScaleBaseBonus?: number;
  // Optional skill-derived growth exponent bonus per affected branch, threaded
  // through so the branch previews match the real MHov / NHov growth.
  readonly growthExponentBonus?: NHovBranchGrowthExponentBonus;
  // Optional active-Wall branch block resolver. The model snapshot builder
  // threads the authoritative wall state through here so the snapshot's
  // canExecute / locked flags stay aligned with the model's execution guard.
  readonly isBranchBlockedByActiveWall?: (branchId: NHovBranchId) => boolean;
  readonly firstExecutionBranchSelectionUnlocked?: boolean;
  readonly executionStock?: NHovSnapshot["executionStock"];
  // Lifts the NUMBER_LIMIT_BOUNDARY clamp on branch effects (granted by
  // `outside-nhov`). Defaults to clamped for callers without achievement context.
  readonly uncapped?: boolean;
  readonly freshnessStaling?: {
    readonly elapsedSinceHovRunMs: number;
  };
}

export function createNHovSnapshot(
  options: CreateNHovSnapshotOptions,
): NHovSnapshot {
  const {
    nHovState,
    runBaseHov,
    hngRankScaleBaseBonus,
    growthExponentBonus,
    isBranchBlockedByActiveWall,
  } = options;
  const firstExecutionBranchSelectionUnlocked =
    options.firstExecutionBranchSelectionUnlocked === true;
  const branchBlockResolver = isBranchBlockedByActiveWall ?? (() => false);
  const offer = resolveNHovCandidateOffer(nHovState.count);
  const rewardPreviews = createNHovBranchRewardPreviews({
    nHovState,
    balance: options.balance,
    runBaseHov,
    offer,
    hngRankScaleBaseBonus,
    growthExponentBonus,
    isBranchBlockedByActiveWall: branchBlockResolver,
    firstExecutionBranchSelectionUnlocked,
    uncapped: options.uncapped,
    freshnessStaling: options.freshnessStaling,
  });
  const derivedBranchLevels = resolveNHovBranchLevelsFromExperience(
    nHovState.branchExperienceByBranch,
  );
  return {
    count: nHovState.count,
    branchExperienceByBranch: nHovState.branchExperienceByBranch,
    derivedBranchLevels,
    // After the first NHov this is permanent; before then a Hov reset that
    // clears `currentLoopHovEarned` re-hides the panel until the new loop
    // earns enough Hov again.
    isUnlocked:
      nHovState.count > 0 || isNHovUnlocked(runBaseHov, options.balance),
    // Keeps snapshot UI state aligned with the model's `executeNHov` guard.
    canExecute: rewardPreviews.some((preview) => preview.canExecute),
    firstExecutionBranchSelectionUnlocked,
    executionStock: options.executionStock ?? null,
    rewardPreviews,
    currentOffer: offer,
  };
}
